import { useMutation, useQueryClient } from '@tanstack/react-query';
import consentTemplatesApi from '@/features/dashboard/consentTemplates/api/consentTemplatesApi.ts';
import { consentTemplatesApiQueryKeys } from '@/features/dashboard/consentTemplates/api/consentTemplatesApi.queryKeys.ts';
import { ConsentTemplateErrorKeys } from '@/features/dashboard/consentTemplates/api/consentTemplateErrors.ts';
import { ConsentTemplateFormData } from '@/features/dashboard/consentTemplates/schemas/consentTemplateSchema.ts';
import { consentTemplateMapper } from '@/features/dashboard/consentTemplates/types/ConsentTemplateDto.mapper.ts';
import { ConsentType } from '@/features/dashboard/consentTemplates/types/ConsentTemplateType.ts';
import { FileAction } from '@/schemas/fileFieldSchema.ts';
import useUploadConsentFile from '@/features/dashboard/consentTemplates/hooks/useUploadConsentFile.ts';
import { isKnownBackendErrorKey } from '@/lib/api/exception.ts';
import { toast } from 'sonner';

export interface UpdateConsentTemplateVars {
  /** Id of the template version currently being edited. */
  id: string;
  data: ConsentTemplateFormData;
}

export default function useUpdateConsentTemplate() {
  const queryClient = useQueryClient();
  const uploadMutation = useUploadConsentFile();

  const mutation = useMutation({
    mutationFn: async ({ id, data }: UpdateConsentTemplateVars) => {
      if (data.type === ConsentType.FILE) {
        const { action } = data.consentFile;
        if (action === FileAction.CREATE || action === FileAction.UPDATE) {
          const result = await uploadMutation.mutateAsync(
            data.consentFile.file!,
          );
          data = {
            ...data,
            consentFile: { ...data.consentFile, fileId: result.fileId },
          };
        }
      }
      const dto = consentTemplateMapper.fromFormToDto(data);
      return consentTemplatesApi.update(id, dto);
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({
        queryKey: consentTemplatesApiQueryKeys.all(),
      });
      // A content change creates a new version with a new id; seed its cache so
      // any further work continues on the current version, not the archived one.
      if (updated.id) {
        queryClient.setQueryData(
          consentTemplatesApiQueryKeys.byId(updated.id),
          updated,
        );
      }
      toast.success('Szablon zgody został zaktualizowany');
    },
    onError: (error: Error) => {
      if (isKnownBackendErrorKey(error, ConsentTemplateErrorKeys.NOT_CURRENT)) {
        toast.error('Nie można zapisać zmian', {
          description:
            'Edytujesz nieaktualną wersję szablonu. Odśwież listę szablonów i spróbuj ponownie.',
        });
        return;
      }
      toast.error('Wystąpił błąd', {
        description:
          error.message || 'Wystąpił błąd podczas aktualizacji szablonu zgody.',
      });
    },
  });

  return {
    ...mutation,
    isPending: mutation.isPending || uploadMutation.isPending,
  };
}
